import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Play, Sparkle } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { GenreRow } from "@/components/GenreRow";
import { fetchVideos } from "@/lib/api";
import { onImgError } from "@/lib/fallback";

const ROWS = [
  { title: "Numberblocks", tag: "numberblocks" },
  { title: "Learn & Play", tag: "learn" },
  { title: "Sing-Alongs", tag: "sing-along" },
  { title: "Family Movies", tag: "family" },
];

export default function Kids() {
  // Hero always spotlights Numberblocks (top pick).
  const { data: heroData } = useQuery({
    queryKey: ["kids-hero"],
    queryFn: () => fetchVideos({ page: 1, limit: 1, content_type: "kids", tag: "numberblocks", sort: "watched" }),
  });
  const hero = heroData?.items?.[0];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ReelVault Kids | Safe, Ad-Free Streaming</title>
        <meta name="description" content="100% free, COPPA-compliant kids entertainment. Zero ads, zero outbound links, just safe streaming." />
      </Helmet>
      <Header />

      {/* Playful hero */}
      <div className="relative overflow-hidden border-b border-white/10" data-testid="kids-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-500/25 via-fuchsia-500/10 to-amber/15" />
        {hero?.default_thumb && (
          <img
            src={hero.default_thumb}
            alt=""
            onError={onImgError}
            className="absolute inset-0 h-full w-full object-cover opacity-25"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="relative mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-16 sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full bg-sky-400/15 border border-sky-400/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300">
            <Sparkle size={13} weight="fill" /> Kids Zone · Always Free & Safe
          </span>
          <h1 className="mt-5 font-serif text-4xl sm:text-6xl tracking-tighter text-white">
            {hero?.title ? hero.title : (
              <>Number<span className="text-sky-400">blocks</span> & Friends</>
            )}
          </h1>
          <p className="mt-3 max-w-xl text-white/70 text-sm sm:text-base leading-relaxed">
            Counting, singing and giggling — a safe, 100% ad-free corner for under-5s. Numberblocks, sing-alongs,
            learning videos and gentle G-rated family movies.
          </p>
          {hero && (
            <Link
              to={`/watch/${hero.video_id}`}
              data-testid="kids-hero-play"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-black font-bold hover:bg-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-300 transition-colors duration-300"
            >
              <Play size={16} weight="fill" /> Watch Now
            </Link>
          )}
        </div>
      </div>

      <main className="mx-auto max-w-[1600px] min-[2200px]:max-w-[2200px] px-5 sm:px-8 py-12 rv-fade" data-testid="kids-rows">
        {ROWS.map((r) => (
          <GenreRow
            key={r.tag}
            title={r.title}
            tag={`kids-${r.tag}`}
            fetchArgs={{ page: 1, limit: 12, content_type: "kids", tag: r.tag }}
          />
        ))}
      </main>
    </div>
  );
}
